/**
 * Auth API calls using the typed `@hostesa/api-client`.
 *
 * These are the only two unauthenticated or session-establishing calls:
 * - `login` — POST /api/v1/auth/login — public
 * - `fetchMe` — GET /api/v1/auth/me — requires a valid token
 *
 * All other API calls live in feature-specific query/mutation hooks.
 */

import type { ApiClient, LoginResponse, MeResponse } from '@hostesa/api-client';
import { ApiError, loginResponseSchema, meResponseSchema } from '@hostesa/api-client';

export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Authenticates with email + password and returns the session token and user.
 *
 * A 401 here always means "invalid credentials" — the backend never reveals
 * whether the email exists (same generic message for missing user vs wrong pw).
 *
 * @throws {ApiError} on 401 (invalid credentials) or any other HTTP error.
 */
export async function login(
  client: ApiClient,
  credentials: LoginCredentials,
): Promise<LoginResponse> {
  // parseAs: 'json' is required because the OpenAPI schema currently declares
  // an empty response body (content?: never) due to the contract gap documented
  // in api-client/src/responses.ts. The backend does return JSON; we validate
  // it ourselves with loginResponseSchema below.
  const { data, error, response } = await client.POST('/api/v1/auth/login', {
    body: credentials,
    parseAs: 'json',
  });

  if (error || !response.ok) {
    throw new ApiError(
      (error as { error?: { code?: string } } | undefined)?.error?.code ?? 'auth_failed',
      (error as { error?: { message?: string } } | undefined)?.error?.message ??
        'Invalid email or password.',
      response.status,
    );
  }

  const parsed = loginResponseSchema.safeParse(data);
  if (!parsed.success) {
    throw new ApiError(
      'invalid_response',
      'Unexpected response from auth server.',
      response.status,
    );
  }

  return parsed.data;
}

/**
 * Loads the currently authenticated user and their memberships.
 *
 * A 401 here means the token is invalid or expired. The caller should redirect
 * to `/login?next=<current-url>`.
 *
 * @throws {ApiError} on 401 (session expired/invalid) or any other HTTP error.
 */
export async function fetchMe(client: ApiClient): Promise<MeResponse> {
  // parseAs: 'json' for the same contract-gap reason as login (see above).
  const { data, error, response } = await client.GET('/api/v1/auth/me', { parseAs: 'json' });

  if (error || !response.ok) {
    throw new ApiError(
      (error as { error?: { code?: string } } | undefined)?.error?.code ?? 'unauthenticated',
      (error as { error?: { message?: string } } | undefined)?.error?.message ??
        'Session invalid or expired.',
      response.status,
    );
  }

  const parsed = meResponseSchema.safeParse(data);
  if (!parsed.success) {
    throw new ApiError(
      'invalid_response',
      'Unexpected response from auth server.',
      response.status,
    );
  }

  return parsed.data;
}
